import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import ServerRackVisualizer from "./ServerRackVisualizer";
import {
  Check,
  ChevronLeft,
  CreditCard,
  Globe2,
  Layers3,
  LifeBuoy,
  Mail,
  Package,
  Server,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Zap,
  Cpu,
  HardDrive,
  Clock,
  Lock,
  Search,
  SlidersHorizontal,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  ChevronDown,
  Info,
  Building2,
  Activity,
  Layers,
  Award
} from "lucide-react";

// Swiss Billing & Cycle Definitions
const cycleLabels = {
  monthly: "Monatlich",
  yearly: "Jährlich (-10%)",
  two_years: "2 Jahre (-15%)"
};

const cycleSuffix = {
  monthly: "/ Monat",
  yearly: "/ Jahr",
  two_years: "/ 2 Jahre"
};

const categoryIcons = {
  hosting: Server,
  webhosting: Server,
  "reseller-hosting": Layers,
  reseller: Layers,
  "server-infrastruktur": Cpu,
  server: Cpu,
  vps: Cpu,
  dedicated: HardDrive,
  domains: Globe2,
  domain: Globe2,
  "e-mail": Mail,
  email: Mail,
  web: Layers3,
  webdesign: Layers3,
  software: Package,
  support: LifeBuoy,
  "managed-services": ShieldCheck,
  "ssl-zertifikate": Lock,
  branding: Award,
  beratung: Activity
};

const isDomainOrAnnual = (product) => {
  if (!product) return false;
  const cat = (product.categoryName || product.category || "").toLowerCase();
  const name = (product.name || "").toLowerCase();
  const slug = (product.slug || "").toLowerCase();
  return (
    cat.includes("domain") ||
    cat === "ssl" ||
    name.includes("domain") ||
    slug.includes("domain") ||
    name.includes("ssl")
  );
};

const priceFor = (product, cycle) => {
  const base = Number(product.unitPrice || 0);
  if (isDomainOrAnnual(product)) {
    if (cycle === "two_years") return base * 2 * 0.95;
    return base; // Already annual (e.g. 15.00 CHF / Jahr for .ch domain)
  }
  if (cycle === "yearly") return base * 12 * 0.9;
  if (cycle === "two_years") return base * 24 * 0.85;
  return base;
};

const normalize = (value = "") =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const matchesCategory = (product, key) => {
  if (key === "all") return true;
  const haystack = `${normalize(product.categoryName)} ${normalize(product.name)} ${normalize(product.description)}`;
  if (key === "web-hosting" || key === "webhosting")
    return haystack.includes("web-hosting") || haystack.includes("webhosting") || (haystack.includes("hosting") && !haystack.includes("reseller"));
  if (key === "reseller-hosting" || key === "reseller")
    return haystack.includes("reseller");
  if (key === "server" || key === "vps" || key === "dedicated")
    return haystack.includes("vds") || haystack.includes("vps") || haystack.includes("server") || haystack.includes("dedicated");
  if (key === "webdesign" || key === "software")
    return haystack.includes("webdesign") || haystack.includes("landing") || haystack.includes("website") || haystack.includes("software");
  if (key === "domains")
    return haystack.includes("domain") || haystack.includes("ssl");
  return normalize(product.categoryName) === key || haystack.includes(key);
};

const featureList = (product) => {
  if (Array.isArray(product.features) && product.features.length) return product.features;
  return (product.description || "")
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 8);
};

export default function ProductMarketplace({ embedded = false }) {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCycles, setSelectedCycles] = useState({});
  const [domainChoices, setDomainChoices] = useState({});
  const [domainInputs, setDomainInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [ordering, setOrdering] = useState("");
  const [message, setMessage] = useState("");
  const [openFaqIdx, setOpenFaqIdx] = useState(null);

  // "One Live Thing" - Interactive Swiss Server & Cloud Configurator State
  const [customStorage, setCustomStorage] = useState(100); // GB NVMe
  const [customCpu, setCustomCpu] = useState(4); // vCPUs
  const [customRam, setCustomRam] = useState(8); // GB RAM
  const [customSla, setCustomSla] = useState("premium"); // standard / premium
  const [customBillingCycle, setCustomBillingCycle] = useState("yearly");

  useEffect(() => {
    document.title = "REDWORK · Schweizer IT-Infrastruktur & Digitale Services";
    const canonical = document.querySelector("link[rel='canonical']") || document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = "https://www.redwork.ch/products";
    document.head.appendChild(canonical);
  }, []);

  useEffect(() => {
    const category = new URLSearchParams(location.search).get("category");
    if (category) setActiveCategory(category);
  }, [location.search]);

  useEffect(() => {
    const load = async () => {
      try {
        const [productRes, categoryRes] = await Promise.all([
          api.get("/products"),
          api.get("/product-categories")
        ]);
        setProducts(productRes.data || []);
        setCategories(categoryRes.data || []);
      } catch (err) {
        console.error("Error loading products:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((cat) => [cat.id, cat])),
    [categories]
  );

  const enriched = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        categoryName: product.categoryName || categoryMap[product.categoryId]?.name || "Services"
      })),
    [products, categoryMap]
  );

  const selectedProduct = productId ? enriched.find((product) => product.id === productId) : null;

  const visibleProducts = useMemo(() => {
    return enriched.filter((product) => {
      const matchCat = matchesCategory(product, activeCategory);
      if (!matchCat) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        product.name.toLowerCase().includes(q) ||
        (product.description || "").toLowerCase().includes(q) ||
        product.categoryName.toLowerCase().includes(q)
      );
    });
  }, [enriched, activeCategory, searchQuery]);

  const marketplaceCategories = useMemo(() => {
    const names = Array.from(new Set(enriched.map((product) => product.categoryName).filter(Boolean)));
    return names.map((name) => ({
      name,
      key: normalize(name),
      count: enriched.filter((product) => product.categoryName === name).length
    }));
  }, [enriched]);

  // Featured flagship product (highlighted at top of catalog)
  const flagshipProduct = useMemo(() => {
    return enriched.find((p) => p.recommended || p.popular) || enriched[0];
  }, [enriched]);

  // Live calculator pricing
  const calculatedCustomPrice = useMemo(() => {
    // Base formula in CHF
    const storageCost = customStorage * 0.12; // 0.12 CHF / GB NVMe
    const cpuCost = customCpu * 7.5; // 7.50 CHF / vCPU
    const ramCost = customRam * 2.8; // 2.80 CHF / GB RAM
    const slaCost = customSla === "premium" ? 45.0 : 0.0;
    const monthlyTotal = 15.0 + storageCost + cpuCost + ramCost + slaCost;

    if (customBillingCycle === "yearly") {
      return (monthlyTotal * 12 * 0.9).toFixed(2);
    }
    return monthlyTotal.toFixed(2);
  }, [customStorage, customCpu, customRam, customSla, customBillingCycle]);

  const handleSelectProduct = (product) => {
    const targetUrl = embedded
      ? `/dashboard/products/${product.id}`
      : `/products/${product.id}`;
    navigate(targetUrl);
  };

  const orderProduct = async (product) => {
    if (!user) {
      sessionStorage.setItem("redwork_pending_product_id", product.id);
      sessionStorage.setItem("redwork_pending_cycle", selectedCycles[product.id] || "monthly");
      navigate("/login");
      return;
    }
    const duration = selectedCycles[product.id] || product.billingCycles?.[0] || "monthly";
    const domainChoice = domainChoices[product.id] || "later";
    const domainName = domainInputs[product.id] || "";

    setOrdering(product.id);
    setMessage("");
    try {
      const res = await api.post("/orders", {
        productId: product.id,
        duration,
        quantity: 1,
        domainChoice,
        domainName
      });
      setMessage(`✓ Bestellung erfolgreich erstellt! Referenz: ${res.data.reference || res.data.id}`);
      setTimeout(() => {
        navigate("/dashboard?tab=services");
      }, 1400);
    } catch (err) {
      setMessage(err.response?.data?.detail || "Bestellung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.");
    } finally {
      setOrdering("");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center space-y-4">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
        <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
          Schweizer Services & Infrastruktur werden geladen...
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // 1. PRODUCT DETAIL & LUXURY CONFIGURATION VIEW (/products/:productId)
  // ---------------------------------------------------------------------------
  if (selectedProduct) {
    const cycle = selectedCycles[selectedProduct.id] || selectedProduct.billingCycles?.[0] || "monthly";
    const features = featureList(selectedProduct);
    const basePrice = priceFor(selectedProduct, cycle);
    const mwstAmount = (basePrice * 0.081).toFixed(2);
    const bruttoAmount = (basePrice * 1.081).toFixed(2);
    const currentDomainChoice = domainChoices[selectedProduct.id] || "later";

    return (
      <div className={embedded ? "p-2" : "relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"}>
        {/* Navigation Breadcrumb */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate(embedded ? "/dashboard/products" : "/products")}
            className="group inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-slate-300 backdrop-blur-md transition hover:border-red-500/50 hover:bg-white/[0.08] hover:text-white"
          >
            <ChevronLeft className="h-4 w-4 transition group-hover:-translate-x-1" />
            Zurück zur Übersicht
          </button>

          <div className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400">
            <span className="flex h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]" />
            Tier-IV Zürich Rechenzentrum · Sofort verfügbar
          </div>
        </div>

        {message && (
          <div className="mb-6 rounded-2xl border border-emerald-500/40 bg-emerald-950/40 p-4 text-sm font-semibold text-emerald-200 backdrop-blur-md">
            {message}
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[1.3fr_0.9fr]">
          {/* Left Column: Product Dossier & Specifications */}
          <div className="space-y-6">
            <div className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-slate-900/80 p-8 shadow-2xl backdrop-blur-xl">
              <div className="absolute right-0 top-0 -mr-16 -mt-16 h-64 w-64 rounded-full bg-red-600/10 blur-3xl" />

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-1 text-[11px] font-bold text-red-400 tracking-wider uppercase">
                  {selectedProduct.categoryName}
                </span>
                {(selectedProduct.recommended || selectedProduct.popular || selectedProduct.badge) && (
                  <span className="rounded-lg bg-gradient-to-r from-red-600 to-amber-600 px-3 py-1 text-[11px] font-bold text-white shadow-md">
                    {selectedProduct.badge || (selectedProduct.recommended ? "Empfohlen" : "Bestseller")}
                  </span>
                )}
                <span className="rounded-lg bg-white/5 border border-white/10 px-3 py-1 text-[11px] font-medium text-slate-300">
                  🇨🇭 100% Swiss Hosted
                </span>
              </div>

              <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                {selectedProduct.name}
              </h1>

              <p className="mt-4 text-base leading-relaxed text-slate-300">
                {selectedProduct.shortDescription || selectedProduct.description}
              </p>

              {/* Hardware / Spec Highlights */}
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 border-y border-white/[0.06] py-6">
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-3 text-center">
                  <Cpu className="mx-auto h-5 w-5 text-red-400 mb-1" />
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Prozessor</p>
                  <p className="text-xs font-bold text-white mt-0.5">AMD EPYC / Intel Xeon</p>
                </div>
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-3 text-center">
                  <HardDrive className="mx-auto h-5 w-5 text-red-400 mb-1" />
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Speicher</p>
                  <p className="text-xs font-bold text-white mt-0.5">100% NVMe Gen4</p>
                </div>
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-3 text-center">
                  <Clock className="mx-auto h-5 w-5 text-red-400 mb-1" />
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Uptime SLA</p>
                  <p className="text-xs font-bold text-white mt-0.5">99.98% Garantiert</p>
                </div>
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-3 text-center">
                  <ShieldCheck className="mx-auto h-5 w-5 text-red-400 mb-1" />
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Standort</p>
                  <p className="text-xs font-bold text-white mt-0.5">Zürich (Schweiz)</p>
                </div>
              </div>

              {/* Feature Checklist */}
              <div className="mt-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
                  Enthaltene Leistungsmerkmale
                </h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 rounded-xl bg-white/[0.02] border border-white/[0.04] p-3 text-xs text-slate-200"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Swiss Quality Seal Banner */}
            <div className="rounded-2xl border border-white/[0.08] bg-slate-900/60 p-6 backdrop-blur-md">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-500/10 border border-red-500/30 text-red-400 font-bold text-lg">
                  CH
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white">Schweizer Sicherheits- & Datenschutzgarantie</h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Ihre Daten unterliegen ausschliesslich dem Schweizer Datenschutzgesetz (DSG) und der DSGVO. Kein CLOUD-Act, keine Weitergabe an Dritte.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Checkout & Configuration Box */}
          <div className="space-y-6">
            <div className="sticky top-28 rounded-3xl border border-white/[0.12] bg-slate-900/95 p-6 shadow-2xl backdrop-blur-2xl">
              <div className="flex items-center justify-between border-b border-white/[0.08] pb-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Konfiguration</p>
                  <h2 className="text-lg font-bold text-white">{selectedProduct.name}</h2>
                </div>
                <div className="text-right">
                  <span className="text-xs text-slate-400">Gesamt</span>
                  <div className="text-2xl font-black text-white">
                    CHF {basePrice.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Billing Cycle Selector */}
              <div className="mt-5 space-y-2">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  Abrechnungszeitraum wählen
                </label>
                <div className="grid gap-2">
                  {(selectedProduct.billingCycles || ["monthly", "yearly", "two_years"]).map((option) => {
                    const price = priceFor(selectedProduct, option);
                    const isSelected = cycle === option;
                    return (
                      <button
                        key={option}
                        onClick={() => setSelectedCycles({ ...selectedCycles, [selectedProduct.id]: option })}
                        className={`flex items-center justify-between rounded-xl border p-3.5 text-left text-xs font-semibold transition ${
                          isSelected
                            ? "border-red-500 bg-red-500/10 text-white shadow-[0_0_15px_rgba(230,57,70,0.15)]"
                            : "border-white/[0.08] bg-white/[0.02] text-slate-300 hover:border-white/20 hover:bg-white/[0.05]"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`h-3.5 w-3.5 rounded-full border flex items-center justify-center ${
                              isSelected ? "border-red-500 bg-red-500" : "border-slate-500"
                            }`}
                          >
                            {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                          </span>
                          <span>{cycleLabels[option] || option}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold">CHF {price.toFixed(2)}</span>
                          <span className="text-[10px] text-slate-400 ml-1">{cycleSuffix[option]}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Domain Options */}
              <div className="mt-6 space-y-2 border-t border-white/[0.08] pt-5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  Domain-Konfiguration
                </label>
                <div className="grid gap-2">
                  {[
                    ["existing", "Bestehende Domain verbinden"],
                    ["new", "Neue Wunschdomain registrieren (.ch, .com, .de)"],
                    ["later", "Domain später konfigurieren"]
                  ].map(([val, label]) => {
                    const isSelected = currentDomainChoice === val;
                    return (
                      <button
                        key={val}
                        onClick={() => setDomainChoices({ ...domainChoices, [selectedProduct.id]: val })}
                        className={`rounded-xl border p-3 text-left text-xs transition ${
                          isSelected
                            ? "border-red-500/60 bg-red-500/10 text-white font-semibold"
                            : "border-white/[0.08] bg-white/[0.02] text-slate-300 hover:border-white/20"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                {currentDomainChoice !== "later" && (
                  <div className="mt-3">
                    <input
                      type="text"
                      placeholder="z.B. ihr-unternehmen.ch"
                      value={domainInputs[selectedProduct.id] || ""}
                      onChange={(e) =>
                        setDomainInputs({ ...domainInputs, [selectedProduct.id]: e.target.value })
                      }
                      className="w-full rounded-xl border border-white/10 bg-slate-950 px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Price Breakdown with Exact Swiss MWST */}
              <div className="mt-6 rounded-2xl border border-white/[0.06] bg-black/40 p-4 space-y-2 text-xs">
                <div className="flex justify-between text-slate-400">
                  <span>Netto-Betrag</span>
                  <span className="font-mono text-white">CHF {basePrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Schweizer MWST (8.1%)</span>
                  <span className="font-mono text-white">CHF {mwstAmount}</span>
                </div>
                <div className="border-t border-white/[0.08] pt-2 flex justify-between font-bold text-sm text-white">
                  <span>Gesamtbetrag (Brutto)</span>
                  <span className="text-red-400 font-mono text-base">CHF {bruttoAmount}</span>
                </div>
              </div>

              {/* Order Button */}
              <Button
                onClick={() => orderProduct(selectedProduct)}
                disabled={ordering === selectedProduct.id || selectedProduct.status === "inactive"}
                className="mt-6 w-full rounded-2xl bg-gradient-to-r from-red-600 to-red-700 py-6 text-sm font-bold text-white shadow-[0_10px_25px_rgba(230,57,70,0.3)] transition hover:from-red-500 hover:to-red-600 active:scale-95 disabled:opacity-50"
              >
                {ordering === selectedProduct.id ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Bestellung wird übermittelt...
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Zahlungspflichtig bestellen
                  </span>
                )}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-4 text-[10px] text-slate-400">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> 256-Bit SSL
                </span>
                <span>·</span>
                <span>TWINT / Stripe / Rechnung</span>
                <span>·</span>
                <span>Sofortige Bereitstellung</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // 2. MAIN STOREFRONT OVERVIEW (/products)
  // ---------------------------------------------------------------------------
  return (
    <div className={embedded ? "space-y-8" : "relative mx-auto max-w-7xl space-y-12 px-4 sm:px-6 lg:px-8"}>
      {/* Hero Showcase Section */}
      {!embedded && (
        <section className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#07090e] to-[#07090e] p-6 sm:p-10 lg:p-12 shadow-2xl">
          <div className="absolute right-0 top-0 -mr-20 -mt-20 h-96 w-96 rounded-full bg-red-600/10 blur-[100px] pointer-events-none" />
          <div className="absolute left-1/3 bottom-0 -mb-20 h-64 w-64 rounded-full bg-blue-600/5 blur-[80px] pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            {/* Left side: Heading & Value Proposition */}
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3.5 py-1 text-xs font-bold text-red-400 shadow-sm">
                <span className="flex h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                🇨🇭 REDWORK ZÜRICH · HIGH-END INFRASTRUKTUR & SERVICES
              </div>

              <h1 className="text-3xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-5xl sm:leading-tight">
                Digitale Exzellenz &{" "}
                <span className="bg-gradient-to-r from-red-500 via-rose-400 to-amber-400 bg-clip-text text-transparent">
                  Schweizer High-End Hosting
                </span>
              </h1>

              <p className="max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Ultraschnelle NVMe Cloud-Server, Reseller-Infrastruktur, massgeschneiderte Webapplikationen und professionelle IT-Services – gehostet im Tier-IV Rechenzentrum Zürich.
              </p>

              {/* Performance Key Indicators Bar */}
              <div className="pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 border-t border-white/[0.08]">
                <div>
                  <p className="text-2xl font-black text-white">99.98%</p>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Verfügbarkeits-SLA</p>
                </div>
                <div>
                  <p className="text-2xl font-black text-white">&lt; 10ms</p>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Schweizer Latenz</p>
                </div>
                <div>
                  <p className="text-2xl font-black text-white">100%</p>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">NVMe Gen4 SSD</p>
                </div>
                <div>
                  <p className="text-2xl font-black text-emerald-400">DSG / DSGVO</p>
                  <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Datenschutzkonform</p>
                </div>
              </div>
            </div>

            {/* Right side: Animated High-Tech Server Rack Visualizer Banner */}
            <div className="lg:col-span-5 w-full">
              <ServerRackVisualizer />
            </div>
          </div>
        </section>
      )}

      {/* Global Status Message */}
      {message && (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-950/40 p-4 text-sm font-semibold text-emerald-200 backdrop-blur-md">
          {message}
        </div>
      )}

      {/* Search & Dynamic Filter Control Bar */}
      <section className="space-y-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Search Box */}
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Services, NVMe Hosting, Software oder Domains durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-2xl border border-white/10 bg-slate-900/80 pl-10 pr-4 py-2.5 text-xs text-white placeholder-slate-500 backdrop-blur-md focus:border-red-500 focus:outline-none transition"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>

          <div className="text-xs text-slate-400 font-medium">
            Zeigt <span className="text-white font-bold">{visibleProducts.length}</span> von {enriched.length} verfügbaren Angeboten
          </div>
        </div>

        {/* Category Pills Bar */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
          <button
            onClick={() => setActiveCategory("all")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold transition whitespace-nowrap ${
              activeCategory === "all"
                ? "bg-red-600 text-white shadow-[0_0_15px_rgba(230,57,70,0.3)]"
                : "border border-white/10 bg-slate-900/60 text-slate-300 hover:border-white/20 hover:text-white"
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span>Alle Services ({enriched.length})</span>
          </button>

          {marketplaceCategories.map((cat) => {
            const Icon = categoryIcons[cat.key] || Package;
            const isActive = activeCategory === cat.key;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold transition whitespace-nowrap ${
                  isActive
                    ? "bg-red-600 text-white shadow-[0_0_15px_rgba(230,57,70,0.3)]"
                    : "border border-white/10 bg-slate-900/60 text-slate-300 hover:border-white/20 hover:text-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5 text-red-400" />
                <span>{cat.name} ({cat.count})</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Featured Flagship Spotlight */}
      {flagshipProduct && activeCategory === "all" && !searchQuery && (
        <section className="relative overflow-hidden rounded-3xl border border-red-500/30 bg-gradient-to-br from-slate-900 via-slate-900/90 to-red-950/30 p-8 shadow-2xl backdrop-blur-xl">
          <div className="absolute top-0 right-0 h-full w-1/3 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-red-600/10 via-transparent to-transparent pointer-events-none" />

          <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr] items-center">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-lg bg-red-500/20 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-red-400">
                <Zap className="h-3 w-3" /> Flagship Solution · Zürich Tier-IV
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
                {flagshipProduct.name}
              </h2>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-xl">
                {flagshipProduct.shortDescription || flagshipProduct.description}
              </p>

              <div className="pt-2 flex flex-wrap gap-2">
                {featureList(flagshipProduct).slice(0, 4).map((feat, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] text-slate-200"
                  >
                    <Check className="h-3 w-3 text-emerald-400" /> {feat}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/40 p-6 text-center lg:text-right backdrop-blur-md">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Startet ab</p>
              <p className="text-3xl sm:text-4xl font-black text-white mt-1">
                CHF {Number(flagshipProduct.unitPrice || 0).toFixed(2)}
                <span className="text-xs font-normal text-slate-400"> / Monat</span>
              </p>
              <p className="text-[11px] text-emerald-400 mt-1">✓ Sofortige Bereitstellung in 60s</p>

              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-end">
                <Button
                  onClick={() => handleSelectProduct(flagshipProduct)}
                  className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs py-5 px-6 shadow-lg shadow-red-600/30"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" /> Jetzt konfigurieren
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Products Grid */}
      <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {visibleProducts.length === 0 ? (
          <div className="col-span-full rounded-3xl border border-dashed border-white/10 bg-slate-900/40 p-12 text-center text-slate-400">
            <Package className="mx-auto h-12 w-12 text-slate-500 mb-3" />
            <h3 className="text-base font-bold text-white">Keine Services gefunden</h3>
            <p className="text-xs text-slate-400 mt-1">
              Versuchen Sie einen anderen Suchbegriff oder wählen Sie eine andere Kategorie.
            </p>
          </div>
        ) : (
          visibleProducts.map((product) => {
            const cycle = selectedCycles[product.id] || product.billingCycles?.[0] || "monthly";
            const price = priceFor(product, cycle);
            const features = featureList(product).slice(0, 5);
            const Icon = categoryIcons[normalize(product.categoryName)] || Package;

            return (
              <article
                key={product.id}
                className="group relative flex flex-col justify-between rounded-3xl border border-white/[0.08] bg-slate-900/60 p-6 shadow-xl backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-red-500/40 hover:shadow-[0_12px_40px_rgba(230,57,70,0.12)]"
              >
                <div>
                  {/* Top Header Badge */}
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-300">
                      <Icon className="h-3 w-3 text-red-400" />
                      {product.categoryName}
                    </span>

                    {(product.recommended || product.popular || product.badge) && (
                      <span className="rounded-md bg-gradient-to-r from-red-600 to-amber-600 px-2 py-0.5 text-[10px] font-black text-white uppercase shadow-sm">
                        {product.badge || (product.recommended ? "Empfohlen" : "Bestseller")}
                      </span>
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold tracking-tight text-white group-hover:text-red-400 transition">
                    {product.name}
                  </h3>

                  <p className="mt-2 text-xs leading-relaxed text-slate-400 line-clamp-2 min-h-[2rem]">
                    {product.shortDescription || product.description}
                  </p>

                  {/* Dynamic Price Block */}
                  <div className="mt-5 border-t border-white/[0.06] pt-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs font-semibold text-slate-400">CHF</span>
                      <span className="text-3xl font-black text-white tracking-tight">
                        {price.toFixed(2)}
                      </span>
                      <span className="text-xs font-medium text-slate-400">
                        {isDomainOrAnnual(product) ? (cycle === "two_years" ? "/ 2 Jahre" : "/ Jahr") : cycleSuffix[cycle]}
                      </span>
                    </div>

                    {/* Quick Billing Cycle Selector Chips */}
                    <div className="mt-3 flex gap-1 rounded-xl border border-white/[0.06] bg-black/30 p-1">
                      {(isDomainOrAnnual(product)
                        ? ["yearly", "two_years"]
                        : (product.billingCycles || ["monthly", "yearly"])
                      ).map((opt) => (
                        <button
                          key={opt}
                          onClick={() => setSelectedCycles({ ...selectedCycles, [product.id]: opt })}
                          className={`flex-1 rounded-lg py-1.5 text-[10px] font-bold transition ${
                            cycle === opt
                              ? "bg-red-600 text-white shadow-sm"
                              : "text-slate-400 hover:text-white"
                          }`}
                        >
                          {opt === "monthly" ? "Monatlich" : opt === "yearly" ? "1 Jahr" : "2 Jahre"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="mt-5 space-y-2.5">
                    {features.map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs text-slate-300">
                        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400 mt-0.5" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Actions */}
                <div className="mt-6 pt-4 border-t border-white/[0.06] grid grid-cols-2 gap-2">
                  <Link
                    to={embedded ? `/dashboard/products/${product.id}` : `/products/${product.id}`}
                    className="w-full inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] py-2.5 text-xs font-bold text-slate-200 transition hover:border-white/25 hover:bg-white/[0.08]"
                  >
                    Details
                  </Link>

                  <Button
                    onClick={() => handleSelectProduct(product)}
                    disabled={ordering === product.id || product.status === "inactive"}
                    className="w-full rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs py-2.5 shadow-md shadow-red-600/20 transition active:scale-95"
                  >
                    <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />
                    Auswählen
                  </Button>
                </div>
              </article>
            );
          })
        )}
      </section>

      {/* "ONE LIVE THING" - Interactive Swiss Cloud & Infrastructure Configurator */}
      <section className="relative overflow-hidden rounded-3xl border border-white/[0.1] bg-slate-900/90 p-8 shadow-2xl backdrop-blur-2xl">
        <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-red-600/10 blur-3xl" />
        <div className="absolute right-0 top-0 h-48 w-48 rounded-full bg-blue-600/5 blur-2xl" />

        <div className="relative z-10 max-w-3xl mb-8 space-y-2">
          <div className="inline-flex items-center gap-1.5 rounded-md bg-white/5 border border-white/10 px-3 py-1 text-[11px] font-bold text-red-400 uppercase tracking-wider">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Schweizer Infrastruktur-Rechner
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
            Massgeschneiderte Enterprise-Ressourcen berechnen
          </h2>
          <p className="text-xs sm:text-sm text-slate-300">
            Wählen Sie Rechenleistung, RAM und NVMe-Speicherplatz nach Mass. Ihr System wird innerhalb weniger Minuten auf Schweizer Hardware bereitgestellt.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] items-center">
          {/* Sliders Configuration Column */}
          <div className="space-y-6">
            {/* Storage Slider */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-slate-300">NVMe Gen4 SSD Speicherplatz</span>
                <span className="text-red-400 font-bold font-mono">{customStorage} GB NVMe</span>
              </div>
              <input
                type="range"
                min="20"
                max="1000"
                step="20"
                value={customStorage}
                onChange={(e) => setCustomStorage(Number(e.target.value))}
                className="w-full accent-red-600 cursor-pointer h-2 bg-slate-800 rounded-lg"
              />
              <div className="flex justify-between text-[10px] text-slate-500">
                <span>20 GB</span>
                <span>250 GB</span>
                <span>500 GB</span>
                <span>1000 GB (1 TB)</span>
              </div>
            </div>

            {/* vCPU Cores Slider */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-slate-300">Dedizierte vCPU Kerne (AMD EPYC)</span>
                <span className="text-red-400 font-bold font-mono">{customCpu} Kerne</span>
              </div>
              <input
                type="range"
                min="1"
                max="16"
                step="1"
                value={customCpu}
                onChange={(e) => setCustomCpu(Number(e.target.value))}
                className="w-full accent-red-600 cursor-pointer h-2 bg-slate-800 rounded-lg"
              />
              <div className="flex justify-between text-[10px] text-slate-500">
                <span>1 Kern</span>
                <span>4 Kerne</span>
                <span>8 Kerne</span>
                <span>16 Kerne</span>
              </div>
            </div>

            {/* RAM Slider */}
            <div className="space-y-2">
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-slate-300">Arbeitsspeicher (ECC DDR5 RAM)</span>
                <span className="text-red-400 font-bold font-mono">{customRam} GB RAM</span>
              </div>
              <input
                type="range"
                min="2"
                max="64"
                step="2"
                value={customRam}
                onChange={(e) => setCustomRam(Number(e.target.value))}
                className="w-full accent-red-600 cursor-pointer h-2 bg-slate-800 rounded-lg"
              />
              <div className="flex justify-between text-[10px] text-slate-500">
                <span>2 GB</span>
                <span>16 GB</span>
                <span>32 GB</span>
                <span>64 GB</span>
              </div>
            </div>

            {/* SLA & Support Level Switch */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setCustomSla("standard")}
                className={`flex-1 rounded-xl border p-3 text-left text-xs transition ${
                  customSla === "standard"
                    ? "border-red-500 bg-red-500/10 text-white font-bold"
                    : "border-white/10 bg-white/[0.02] text-slate-400"
                }`}
              >
                <div className="font-bold text-white">Standard 24/7 SLA</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Automatisches Monitoring & Notfall-Support</div>
              </button>

              <button
                onClick={() => setCustomSla("premium")}
                className={`flex-1 rounded-xl border p-3 text-left text-xs transition ${
                  customSla === "premium"
                    ? "border-red-500 bg-red-500/10 text-white font-bold"
                    : "border-white/10 bg-white/[0.02] text-slate-400"
                }`}
              >
                <div className="font-bold text-white">Dedicated Engineer SLA</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Persönlicher Schweizer Betreuer (+45 CHF)</div>
              </button>
            </div>
          </div>

          {/* Real-Time Price Output Box */}
          <div className="rounded-3xl border border-white/10 bg-black/60 p-6 text-center lg:text-right backdrop-blur-xl space-y-4">
            <span className="rounded-full bg-red-500/10 border border-red-500/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-red-400">
              Live Investitionsberechnung
            </span>

            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Monatlicher Richtpreis</p>
              <div className="text-4xl sm:text-5xl font-black text-white mt-1">
                CHF {calculatedCustomPrice}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">
                inkl. redundanter Schweizer Backups & AutoSSL
              </p>
            </div>

            <div className="flex justify-center lg:justify-end gap-2">
              <button
                onClick={() => setCustomBillingCycle("monthly")}
                className={`rounded-lg px-3 py-1 text-[11px] font-bold ${
                  customBillingCycle === "monthly" ? "bg-white text-slate-950" : "bg-white/10 text-slate-300"
                }`}
              >
                Monatlich
              </button>
              <button
                onClick={() => setCustomBillingCycle("yearly")}
                className={`rounded-lg px-3 py-1 text-[11px] font-bold ${
                  customBillingCycle === "yearly" ? "bg-red-600 text-white" : "bg-white/10 text-slate-300"
                }`}
              >
                1 Jahr (-10%)
              </button>
            </div>

            <Button
              onClick={() => navigate("/hosting")}
              className="w-full rounded-2xl bg-red-600 hover:bg-red-700 py-6 text-xs font-bold text-white shadow-xl shadow-red-600/30"
            >
              Individuelle Konfiguration bestellen
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Trust & Guarantee Grid */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-white/[0.08] bg-slate-900/60 p-5 backdrop-blur-md">
          <Building2 className="h-6 w-6 text-red-500 mb-2" />
          <h4 className="text-sm font-bold text-white">Tier-IV Zürich Datacenter</h4>
          <p className="text-xs text-slate-400 mt-1">
            Modernste Serverhardware, Notstromaggregate und direkte Anbindung an den SwissIX.
          </p>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-slate-900/60 p-5 backdrop-blur-md">
          <ShieldCheck className="h-6 w-6 text-emerald-400 mb-2" />
          <h4 className="text-sm font-bold text-white">100% DSG / DSGVO Konform</h4>
          <p className="text-xs text-slate-400 mt-1">
            Strengste Schweizer Datenschutzstandards. Keine Cloud-Act-Risiken oder Datenübertragungen.
          </p>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-slate-900/60 p-5 backdrop-blur-md">
          <Zap className="h-6 w-6 text-amber-400 mb-2" />
          <h4 className="text-sm font-bold text-white">Instant Deployment</h4>
          <p className="text-xs text-slate-400 mt-1">
            Vollautomatisches Setup nach Zahlungseingang via TWINT, Stripe oder Schweizer QR-Rechnung.
          </p>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-slate-900/60 p-5 backdrop-blur-md">
          <LifeBuoy className="h-6 w-6 text-blue-400 mb-2" />
          <h4 className="text-sm font-bold text-white">Persönlicher Support</h4>
          <p className="text-xs text-slate-400 mt-1">
            Keine Chatbots. Direkter Draht zu qualifizierten Schweizer System-Engineers in Zürich.
          </p>
        </div>
      </section>

      {/* Executive FAQ Accordion */}
      <section className="rounded-3xl border border-white/[0.08] bg-slate-900/60 p-8 backdrop-blur-xl">
        <div className="max-w-2xl mb-6">
          <h3 className="text-xl font-bold text-white">Häufig gestellte Fragen (FAQ)</h3>
          <p className="text-xs text-slate-400 mt-1">
            Antworten zu Bereitstellung, Zahlungsmodalitäten und Skalierbarkeit unserer Systeme.
          </p>
        </div>

        <div className="space-y-3">
          {[
            {
              q: "Wie schnell wird mein Server oder Hosting-Paket bereitgestellt?",
              a: "Nach erfolgreichem Zahlungseingang via TWINT oder Kreditkarte wird Ihr Account vollautomatisch innerhalb von 60 Sekunden auf unseren Servern in Zürich provisioniert. Sie erhalten umgehend Ihre Zugangsdaten per E-Mail."
            },
            {
              q: "Sind automatische Backups in den Paketen enthalten?",
              a: "Ja. Bei allen Webhosting- und Cloud-Paketen erstellen wir täglich automatische, verschlüsselte Sicherungen in ein zweites, geografisch getrenntes Schweizer Rechenzentrum."
            },
            {
              q: "Kann ich bestehende Domains zu REDWORK transferieren?",
              a: "Selbstverständlich. Im Bestellprozess können Sie einfach Ihre bestehende Domain angeben oder einen EPP/Auth-Code für den nahtlosen Transfer hinterlegen. Unser Support unterstützt Sie kostenfrei beim Umzug."
            },
            {
              q: "Welche Zahlungsmethoden werden akzeptiert?",
              a: "Wir unterstützen Schweizer TWINT, alle gängigen Kreditkarten (Visa, Mastercard, Amex), Apple Pay, Google Pay sowie Schweizer QR-Rechnung mit 30 Tagen Zahlungsziel für Firmenkunden."
            }
          ].map((item, idx) => {
            const isOpen = openFaqIdx === idx;
            return (
              <div
                key={idx}
                className="overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] transition"
              >
                <button
                  onClick={() => setOpenFaqIdx(isOpen ? null : idx)}
                  className="flex w-full items-center justify-between p-4 text-left text-xs sm:text-sm font-bold text-white hover:text-red-400"
                >
                  <span>{item.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                      isOpen ? "rotate-180 text-red-400" : "text-slate-400"
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="border-t border-white/[0.04] p-4 text-xs leading-relaxed text-slate-300 bg-black/20">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
